import { Controller } from "@hotwired/stimulus"

/**
 * WhnFormController
 *
 * Handles image dimension checking and file preview for the WHN ad form.
 * Works in conjunction with the image-preview controller for file selection.
 * Checks dimensions of both existing images (edit mode) and new file uploads.
 *
 * @example
 *   <form data-controller="whn-form">
 *     <img data-whn-form-target="image" src="...">
 *     <input type="file" data-whn-form-target="fileInput" data-action="change->whn-form#checkNewFileDimensions">
 *     <span data-whn-form-target="dimensions"></span>
 *     <div data-whn-form-target="feedback"
 *          data-retina-ready="..."
 *          data-size-ok="..."
 *          data-too-small="..."
 *          data-width-warning="..."
 *          data-load-error="..."></div>
 *   </form>
 */
export default class extends Controller {
  static targets = [
    "image",
    "fileInput",
    "dimensions",
    "feedback",
    "errorAlert",
    "errorHeader",
    "errorList"
  ]

  // WHN ads are displayed at full container width (~1140px on desktop)
  // These dimensions are based on the Bootstrap container width
  static DISPLAY_WIDTH = 1140
  static DISPLAY_HEIGHT = 400  // Approximate based on typical ad aspect ratios
  static RETINA_WIDTH = 2280   // 2x for Retina
  static RETINA_HEIGHT = 800
  // Minimum acceptable width (below this, image will look pixelated)
  static MIN_WIDTH = 900

  connect() {
    this.checkExistingImageDimensions()
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Form Validation (on submit)
  // ─────────────────────────────────────────────────────────────────────────────

  validateForm(event) {
    const form = this.element
    const errors = []
    const reportedInputs = new Set()

    // Collect validation errors from required fields
    const requiredInputs = form.querySelectorAll('[required]')
    requiredInputs.forEach(input => {
      if (!input.value || input.value.trim() === '') {
        const label = this.findLabelForInput(input)
        errors.push(label ? `${label} is required` : 'This field is required')
        reportedInputs.add(input)
      }
    })

    // Check HTML5 constraint validation (skip inputs already reported above)
    if (!form.checkValidity()) {
      const invalidInputs = form.querySelectorAll(':invalid')
      invalidInputs.forEach(input => {
        if (reportedInputs.has(input)) return
        if (input.validationMessage && !errors.includes(input.validationMessage)) {
          const label = this.findLabelForInput(input)
          errors.push(label ? `${label}: ${input.validationMessage}` : input.validationMessage)
        }
      })
    }

    if (errors.length > 0) {
      event.preventDefault()
      this.displayClientErrors(errors)
    }
  }

  findLabelForInput(input) {
    // Try to find associated label
    if (input.id) {
      const label = document.querySelector(`label[for="${input.id}"]`)
      if (label) return label.textContent.replace(/\*$/, '').trim()
    }
    // Try to find parent label
    const parentLabel = input.closest('label')
    if (parentLabel) return parentLabel.textContent.replace(/\*$/, '').trim()
    // Try aria-label (for inputs in card-based layouts without <label> elements)
    if (input.getAttribute('aria-label')) return input.getAttribute('aria-label')
    // Try placeholder as fallback
    if (input.placeholder) return input.placeholder
    return null
  }

  displayClientErrors(errors) {
    if (!this.hasErrorAlertTarget || !this.hasErrorListTarget) return

    const errorAlert = this.errorAlertTargets.find(el => el.classList.contains('d-none'))
    if (!errorAlert) return

    // Update header with correct pluralization
    if (this.hasErrorHeaderTarget) {
      const headerKey = errors.length === 1 ? 'errorsHeaderOne' : 'errorsHeaderOther'
      this.errorHeaderTarget.textContent = errorAlert.dataset[headerKey] || `${errors.length} error(s) prohibited this record from being saved:`
    }

    // Populate error list
    this.errorListTarget.innerHTML = ''
    errors.forEach(error => {
      const li = document.createElement('li')
      li.textContent = error
      this.errorListTarget.appendChild(li)
    })

    // Show the alert
    errorAlert.classList.remove('d-none')
    errorAlert.classList.add('show')

    // Scroll to alert
    errorAlert.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Existing Image Dimension Checker (for edit mode)
  // ─────────────────────────────────────────────────────────────────────────────

  checkExistingImageDimensions() {
    if (!this.hasImageTarget || !this.hasDimensionsTarget) return

    const imgElement = this.imageTarget

    // Skip if no src (new record)
    const imgSrc = imgElement.src || imgElement.dataset.src
    if (!imgSrc || imgSrc === '') return

    const handleLoad = () => {
      if (imgElement.naturalWidth > 0) {
        this.displayDimensions(imgElement.naturalWidth, imgElement.naturalHeight)
      }
    }

    const handleError = () => {
      this.showImageError()
    }

    imgElement.addEventListener('load', handleLoad, { once: true })
    imgElement.addEventListener('error', handleError, { once: true })

    // Check if already loaded or errored (use requestAnimationFrame to ensure DOM is ready)
    requestAnimationFrame(() => {
      if (imgElement.complete) {
        if (imgElement.naturalWidth > 0) {
          handleLoad()
        } else {
          // Image is complete but has no dimensions = broken/404
          handleError()
        }
      }
    })
  }

  showImageError() {
    if (this.hasDimensionsTarget) {
      const errorText = this.hasFeedbackTarget ? this.feedbackTarget.dataset.loadError : 'Image not found'
      this.dimensionsTarget.textContent = errorText
      this.dimensionsTarget.classList.add("text-danger")
    }
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.innerHTML = ''
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // New File Upload Dimension Checker
  // ─────────────────────────────────────────────────────────────────────────────

  checkNewFileDimensions() {
    if (!this.hasFileInputTarget || !this.hasDimensionsTarget) return

    const fileInput = this.fileInputTarget
    if (!fileInput.files || !fileInput.files[0]) return

    const file = fileInput.files[0]

    // Verify it's an image
    if (!file.type.startsWith('image/')) {
      this.showImageError()
      return
    }

    // Create object URL to load the image
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      this.displayDimensions(img.naturalWidth, img.naturalHeight)
      URL.revokeObjectURL(objectUrl)
    }

    img.onerror = () => {
      this.showImageError()
      URL.revokeObjectURL(objectUrl)
    }

    img.src = objectUrl
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Dimension Display & Feedback
  // ─────────────────────────────────────────────────────────────────────────────

  displayDimensions(width, height) {
    this.dimensionsTarget.textContent = `${width} × ${height} px`
    this.dimensionsTarget.classList.remove("text-danger")
    this.updateSizeFeedback(width, height)
  }

  updateSizeFeedback(width, height) {
    if (!this.hasFeedbackTarget) return

    const feedback = this.feedbackTarget

    if (width >= this.constructor.RETINA_WIDTH) {
      // Excellent - Retina ready
      feedback.innerHTML = `<span class="badge bg-success"><i class="fa-solid fa-circle-check me-1"></i>${feedback.dataset.retinaReady}</span>`
    } else if (width >= this.constructor.DISPLAY_WIDTH) {
      // Good - OK for standard displays but could be better for Retina
      feedback.innerHTML = `<span class="badge bg-info"><i class="fa-solid fa-info-circle me-1"></i>${feedback.dataset.sizeOk}</span>`
    } else if (width >= this.constructor.MIN_WIDTH) {
      // Warning - width is acceptable but not ideal
      feedback.innerHTML = `<span class="badge bg-warning text-dark"><i class="fa-solid fa-exclamation-triangle me-1"></i>${feedback.dataset.widthWarning}</span>`
    } else {
      // Too small - will look pixelated
      feedback.innerHTML = `<span class="badge bg-danger"><i class="fa-solid fa-times-circle me-1"></i>${feedback.dataset.tooSmall}</span>`
    }
  }
}
